import type { ChildrenAges } from '@pcln/traveler-selection'
import { omit } from 'ramda'

import { type GA4PageNameType } from '@/ga4types'
import { formatDate } from '@/shared-utils/date-helper'
import { HOTEL_SEARCH_DATA, persistSearchData } from '@/shared-utils/local-forage-helper'
import packagesSearch from '@/shared-utils/packages-search'

import { CAR, FLIGHT } from '../../Packages/SearchForm/TripTypes'
import { PromoDeal, STAY } from '../constants'
import fireHotelSubmitEvent from '../ga4'
import type {
  FilterCategoryHotel,
  FilterValueHotel,
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
} from '../types'

const initialFilterState = {
  amenities: new Set<FilterValueHotel<'amenities'>>(),
  hotelStarLevel: new Set<FilterValueHotel<'hotelStarLevel'>>(),
  guestRating: new Set<FilterValueHotel<'guestRating'>>(),
  propertyTypes: new Set<FilterValueHotel<'propertyTypes'>>(),
  AMENITY: new Set<FilterValueHotel<'AMENITY'>>(),
  HOTEL_STAR_LEVEL: new Set<FilterValueHotel<'HOTEL_STAR_LEVEL'>>(),
  GUEST_RATING: new Set<FilterValueHotel<'GUEST_RATING'>>(),
  PROPERTY_TYPE: new Set<FilterValueHotel<'PROPERTY_TYPE'>>(),
  ALL_BRANDS: new Set<FilterValueHotel<'ALL_BRANDS'>>(),
  PROPERTY_THEME: new Set<FilterValueHotel<'PROPERTY_THEME'>>(),
} as const

export const initialState: HOTEL_FORM_STATE_TYPE = {
  endLocation: null,
  startLocation: null,
  travelers: {
    adults: 2,
    children: 0,
    lapInfants: 0,
    childrenAges: {},
    rooms: 1,
  },
  startDate: '',
  endDate: '',
  tripType: STAY,
  mapView: false,
  selectedTopFilters: { ...initialFilterState },
}

export const fields = [
  'startLocation',
  'endLocation',
  'startDate',
  'endDate',
  'travelers',
  'tripType',
] as const

const getHotelMultiOccParam = ({
  adultCount,
  childrenAges,
}: {
  adultCount: number
  childrenAges: ChildrenAges
}) => {
  let childrenCount = 0
  let childrenAgesParam = ''

  if (Array.isArray(childrenAges)) {
    childrenCount = childrenAges.length
    childrenAgesParam = childrenAges.join(',')
  } else if (typeof childrenAges === 'object') {
    const ages = Object.values(childrenAges)
    childrenCount = ages.length
    childrenAgesParam = ages.join(',')
  }

  return `/adults/${adultCount}${childrenCount > 0 ? `/children/${childrenAgesParam}` : ''}`
}

const baseURL = 'https://findgroupdeals.hotelplanner.com/Priceline.cfm?City='
const basePartnerURL = 'https://priceline.partners.engine.com/p/create-a-trip?City='

export const groupDealsURL = ({
  location,
  startDate,
  endDate,
  isPartnersURL,
}: {
  location: string
  startDate: string
  endDate: string
  isPartnersURL?: boolean
}) => {
  return `${isPartnersURL ? basePartnerURL : baseURL}${encodeURIComponent(
    location,
  )}&CheckIn=${encodeURIComponent(formatDate({ dateStr: startDate }))}&CheckOut=${encodeURIComponent(formatDate({ dateStr: endDate }))}`
}

const dealTypeUrlMap = {
  [PromoDeal.EXPRESS_DEAL]: '/sopq',
  [PromoDeal.NHA]: '/nha',
}

function handleFilterParams(filterType: FilterCategoryHotel, filters: string[] | string) {
  // AMENITIES
  if (filterType === 'amenities' || filterType === 'AMENITY') {
    const filterValues = Array.isArray(filters) ? filters.join(',') : filters
    return `amenities=${encodeURIComponent(filterValues)}`
  }

  // GUEST RATING
  if (filterType === 'guestRating' || filterType === 'GUEST_RATING') {
    const filterValues = Array.isArray(filters) ? filters.join(',') : filters
    return `minGuestRating=${encodeURIComponent(filterValues)}`
  }

  // PROPERTY TYPES
  if (filterType === 'propertyTypes' || filterType === 'PROPERTY_TYPE') {
    const filterValues = Array.isArray(filters) ? filters.join(',') : filters
    return `propertytypes=${encodeURIComponent(filterValues)}`
  }

  // HOTEL STAR LEVEL
  if (filterType === 'hotelStarLevel' || filterType === 'HOTEL_STAR_LEVEL') {
    // Flatten comma-joined values (`["4,4.5,5"]` from AI) before taking the min —
    // `Number("4,4.5,5")` is NaN and `stars.slice(NaN)` expands to every rating.
    const rawValues = Array.isArray(filters) ? filters : [filters]
    const starsTonumbers = rawValues
      .flatMap((value) => String(value).split(','))
      .map(Number)
      .filter((n) => Number.isFinite(n))

    if (starsTonumbers.length === 0) return

    const minStarNum = Math.min(...starsTonumbers)
    const stars = ['1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5']
    const startIndex = minStarNum * 2 - 2
    if (startIndex < 0 || startIndex >= stars.length) return

    const starsString = stars.slice(startIndex)
    return `starratings=${encodeURIComponent(starsString.join(','))}`
  }

  // BRANDS
  if (filterType === 'ALL_BRANDS') {
    const filterValues = Array.isArray(filters) ? filters.join(',') : filters
    return `brands=${encodeURIComponent(filterValues)}`
  }

  // PROPERTY_THEME
  if (filterType === 'PROPERTY_THEME') {
    const filterValues = Array.isArray(filters) ? filters.join(',') : filters
    return `themeids=${encodeURIComponent(filterValues)}`
  }
}

export function formatHotelsURL({
  endLocation,
  startDate,
  endDate,
  travelers,
  useCurrentLocation,
  dealType,
  SEMSearch,
  SEMSEARCH,
  mapView,
  selectedTopFilters,
  userLatitude,
  userLongitude,
  refId,
  refClickId,
  minPrice,
  maxPrice,
}: HOTEL_FORM_SUBMIT_TYPE & {
  useCurrentLocation?: boolean
  userLatitude?: number
  userLongitude?: number
}) {
  if (endLocation === null) {
    return
  }
  const { type: searchType } = endLocation
  const updatedSearchType = searchType === 'HOTEL' ? 'CITY' : searchType
  let location =
    (searchType === 'HOTEL' ? endLocation.cityID : endLocation.id) || encodeURIComponent(endLocation.itemName)

  if (searchType === 'AI_QUERY') {
    location = endLocation.cityID || encodeURIComponent(endLocation.itemName)
  }

  const dealTypeParam = dealType === 'EXPRESS_DEAL' || dealType === 'NHA' ? dealTypeUrlMap[dealType] : ''

  const multiOcc = getHotelMultiOccParam({
    adultCount: travelers.adults,
    childrenAges: travelers.childrenAges,
  })
  const queries = []

  if (searchType === 'NEIGHBOURHOOD' && endLocation.id) {
    queries.push(`clusters=${endLocation.id}`)
  }

  if (searchType === 'HOTEL' && !dealTypeParam) {
    queries.push(`preferredhotelids=${endLocation.id}`)
  }
  if (SEMSearch) {
    queries.push(`SEMSearch=${SEMSearch}`)
  }
  if (SEMSEARCH) {
    queries.push(`SEMSEARCH=${SEMSEARCH}`)
  }

  if (searchType !== 'AI_QUERY') {
    ;(Object.keys(selectedTopFilters) as Array<FilterCategoryHotel>).forEach((key) => {
      const filters = Array.from(selectedTopFilters[key])
      if (filters.length > 0) {
        const codeFilters = filters.map((filter) => filter.split('_')[1])
        const filterParams = handleFilterParams(key, codeFilters)
        if (filterParams) {
          queries.push(filterParams)
        }
      }
    })
  }

  if (endLocation?.filters) {
    // One param per category — repeating `amenities=` would drop all but one value.
    const valuesByFilterType = new Map<string, string[]>()

    endLocation.filters.forEach(({ filterType, filterValue }) => {
      const values = valuesByFilterType.get(filterType) ?? []
      const incoming = Array.isArray(filterValue) ? filterValue : [filterValue]

      incoming.forEach((value) => {
        if (value && !values.includes(value)) {
          values.push(value)
        }
      })

      valuesByFilterType.set(filterType, values)
    })

    valuesByFilterType.forEach((values, filterType) => {
      const filterParams = handleFilterParams(filterType as FilterCategoryHotel, values)
      if (filterParams) {
        queries.push(filterParams)
      }
    })
  }

  if (typeof minPrice === 'number') {
    queries.push(`minprice=${minPrice}`)
  }
  if (typeof maxPrice === 'number') {
    queries.push(`maxprice=${maxPrice}`)
  }

  if (refId?.length && refClickId?.length) {
    queries.push(`refid=${refId}`)
    queries.push(`refclickid=${refClickId}`)
  }

  const query = queries.length ? `?${queries.join('&')}` : ''

  const urlDatePart = startDate && endDate ? `/from/${startDate}/to/${endDate}` : ''

  const mapViewPath = mapView ? 'mapview/' : ''
  if (useCurrentLocation && endLocation && userLatitude && userLongitude) {
    return `/relax/geo/${endLocation.itemName}/${userLatitude}/${userLongitude}/from/${startDate}/to/${endDate}/rooms/${travelers.rooms}${multiOcc}${query}`
  }
  if (updatedSearchType === 'POI' || updatedSearchType === 'AIRPORT') {
    return `/relax/${mapViewPath}near/${updatedSearchType.toLowerCase()}/${location}${urlDatePart}/rooms/${
      travelers.rooms
    }${dealTypeParam}${multiOcc}${query}`
  }
  const locationPath =
    searchType === 'NEIGHBOURHOOD' ? endLocation.cityID || encodeURIComponent(endLocation.itemName) : location

  return `/relax/${mapViewPath}in/${locationPath}${urlDatePart}/rooms/${travelers.rooms}${dealTypeParam}${multiOcc}${query}`
}

export function handleSubmitForHotels(values: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) {
  const { startDate, endDate, endLocation, tripType, startLocation, travelers } = values

  const multiOccFields = {
    adultCount: travelers.adults,
    childrenCount: travelers.children,
    childrenAges: travelers.childrenAges,
  }
  const isPackageSearch = tripType.includes(FLIGHT) || tripType.includes(CAR)
  const hasFlight = tripType.includes(FLIGHT)

  fireHotelSubmitEvent(initialState, values, pageName, values.searchItemName, values.prefillAnalytics)

  if (isPackageSearch) {
    const basePackageSearchDetails = {
      roomCount: travelers.rooms,
      startDate,
      endDate,
      endLocationObject: endLocation,
      tripType,
      isPartialStayDateValid: false,
      ...multiOccFields,
    }
    const flightSearchDetails = {
      startLocationObject: startLocation,
      ...multiOccFields,
    }

    const packageSearchDetails = {
      ...basePackageSearchDetails,
      ...(hasFlight ? flightSearchDetails : {}),
    }
    packagesSearch(packageSearchDetails)
  } else {
    const url = formatHotelsURL(values)
    if (url) {
      window.open(url)
    }
  }
  void persistSearchData({ key: HOTEL_SEARCH_DATA, recentSearchData: omit(['prefillAnalytics'], values) })
}

export const handleSubmitForExpressDeals = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.EXPRESS_DEAL,
  })
  window.open(url)
}

export const handleSubmitForNHA = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.NHA,
  })
  window.open(url)
}
